/**
 * §A.5 · Los interruptores de un club.
 *
 * ## Para qué sirve esto de verdad
 *
 * Dos cosas, y las dos las dice el contrato: **abrir una función a un club piloto
 * antes que al resto**, y **apagarla en uno solo** cuando da problemas, sin desplegar
 * nada ni tocar a los demás. Lo segundo es lo que se usa a las once de la noche.
 *
 * ## La nota no es opcional en la práctica
 *
 * «Dentro de tres meses, un interruptor puesto sin nota es un interruptor que nadie
 * se atreve a quitar.» El DTO la deja opcional; esta pantalla la pide y lo explica,
 * porque el coste de no ponerla lo paga otra persona medio año después.
 *
 * No se **obliga**: a las once de la noche, apagar algo que está rompiendo un club no
 * puede depender de redactar una frase. Se pide, se explica, y se avisa de lo que
 * queda sin ella.
 *
 * ## Crear un interruptor es escribir su nombre
 *
 * No hay catálogo ni alta previa: el `PUT` crea la fila si no existe. Así que el
 * formulario acepta un nombre nuevo y valida **la forma** —`^[a-z0-9_.]+$`— antes de
 * enviar, que es lo único comprobable en el cliente.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import { nombreDeInterruptor, type Interruptor } from '@/backoffice/tipos';

/** La forma que exige el DTO. Lo único que se puede comprobar sin el servidor. */
const FORMA = /^[a-z0-9_.]+$/;

export function InterruptoresDelClub({ clubId }: { clubId: string }) {
  const cache = useQueryClient();
  const [poniendo, setPoniendo] = useState(false);
  const [nombre, setNombre] = useState('');
  const [nota, setNota] = useState('');
  const [encendido, setEncendido] = useState(true);
  const [problema, setProblema] = useState<Problema | null>(null);

  const flags = useQuery({
    queryKey: ['admin', 'flags', clubId],
    queryFn: async () => {
      const { data, error } = await api.v1.admin
        .academies({ id: clubId })
        ['feature-flags'].get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Interruptor[];
    },
  });

  const poner = useMutation({
    mutationFn: async (cambio: {
      flag: string;
      enabled: boolean;
      note: string | null;
    }) => {
      const r = await api.v1.admin
        .academies({ id: clubId })
        ['feature-flags'].put(cambio);
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Interruptor;
    },
    onSuccess: () => {
      setPoniendo(false);
      setNombre('');
      setNota('');
      void cache.invalidateQueries({ queryKey: ['admin', 'flags'] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const formaMal = nombre.trim() !== '' && !FORMA.test(nombre.trim());

  return (
    <>
      <h2 className="seccion">Interruptores de este club</h2>

      {problema && <PanelError problema={problema} />}

      {flags.isPending && <Cargando filas={2} etiqueta="Cargando" />}

      {flags.isError && (
        <PanelError
          problema={aProblema(flags.error)}
          reintentar={() => void flags.refetch()}
        />
      )}

      {flags.data &&
        (flags.data.length === 0 ? (
          <p className="tenue">
            Este club no tiene ninguna decisión propia: le vale lo que esté
            puesto para todos. Es lo normal.
          </p>
        ) : (
          <ul className="papeles">
            {flags.data.map((f) => (
              <li key={f.id}>
                <div className="aviso-cabeza">
                  <strong>{nombreDeInterruptor(f.flag)}</strong>
                  <span
                    className={`pastilla ${
                      f.enabled ? 'pastilla-bien' : 'pastilla-paused'
                    } pastilla-left`}
                  >
                    {f.enabled ? 'Encendido' : 'Apagado'}
                  </span>
                  {/* El nombre técnico al lado: es lo que se busca en el código. */}
                  <code className="tenue">{f.flag}</code>
                </div>

                {f.note ? (
                  <p>{f.note}</p>
                ) : (
                  <p className="tenue">
                    Sin nota. Dentro de tres meses nadie va a saber por qué está
                    así.
                  </p>
                )}

                <div className="aviso-pie">
                  <span className="tenue">
                    Cambiado {fechaYHora(f.updated_at)}
                  </span>
                  <button
                    type="button"
                    className="btn-plano"
                    disabled={poner.isPending}
                    onClick={() => {
                      setProblema(null);
                      poner.mutate({
                        flag: f.flag,
                        enabled: !f.enabled,
                        // La nota se conserva: el `PUT` sustituye la fila entera y
                        // mandar `null` aquí borraría el porqué al apagar y encender.
                        note: f.note,
                      });
                    }}
                  >
                    {f.enabled ? 'Apagarlo' : 'Encenderlo'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ))}

      {poniendo ? (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            setProblema(null);
            poner.mutate({
              flag: nombre.trim(),
              enabled: encendido,
              note: nota.trim() === '' ? null : nota.trim(),
            });
          }}
        >
          <div className="rejilla">
            <label>
              Qué interruptor
              <input
                required
                maxLength={60}
                placeholder="segunda_mano.activo"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </label>

            <label>
              Cómo queda
              <select
                value={encendido ? 'si' : 'no'}
                onChange={(e) => setEncendido(e.target.value === 'si')}
              >
                <option value="si">Encendido para este club</option>
                <option value="no">Apagado para este club</option>
              </select>
            </label>
          </div>

          <label>
            Por qué
            <input
              maxLength={300}
              placeholder="Piloto de segunda mano. Lo pidió el club el 12/09."
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
          </label>

          <p className="tenue">
            {formaMal ? (
              <strong>
                El nombre va en minúsculas, con puntos o guiones bajos:
                «muro.fotos», «segunda_mano.activo». Es la forma que acepta el
                servidor.
              </strong>
            ) : nota.trim() === '' ? (
              <strong>
                Pon el porqué. Un interruptor sin nota es un interruptor que
                nadie se atreve a quitar dentro de tres meses.
              </strong>
            ) : (
              'Si el nombre no existe todavía, se crea con esta decisión.'
            )}
          </p>

          <div className="acciones">
            <button
              type="submit"
              disabled={poner.isPending || formaMal || nombre.trim() === ''}
            >
              {poner.isPending ? 'Guardando…' : 'Poner el interruptor'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={poner.isPending}
              onClick={() => {
                setPoniendo(false);
                setProblema(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      ) : (
        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-plano"
            onClick={() => setPoniendo(true)}
          >
            Poner un interruptor
          </button>
        </div>
      )}
    </>
  );
}
